import {
  BucketLocationType,
  BucketMovementType,
  type BucketBatchItem,
  type BucketBatchResult,
  type BucketMovementResult,
  type BucketWarning,
  type CreateBucketMovementInput,
  type OnDemandPeriod,
} from '@/integrations/agreements/client';
import {
  countBucketOperations,
  toBucketCount,
  type BucketQuantities,
  type BucketQuantityField,
} from './bucketQuantities';

/** Estado do pedido de caçamba, no formato que a tela edita. */
export interface BucketRequestFormState extends BucketQuantities {
  /**
   * Número de cada caçamba a retirar, na ordem das retiradas — sempre do tamanho de `removal`.
   * Obrigatório quando o contrato controla número; nos outros, vai só o que foi preenchido.
   */
  removalBucketNumbers: string[];
  /** Quando entra alguma caçamba (colocação), onde ela fica. */
  locationType: BucketLocationType | null;
  /** `YYYY-MM-DD`. */
  plannedDate: string;
  /** Turno da operação; nulo = sem preferência. */
  period: OnDemandPeriod | null;
  contactName: string;
  contactPhone: string;
  observations: string;
  /**
   * Fotos de evidência já enviadas (URLs do upload); valem para todas as operações. O gerador
   * manda ao menos uma (EDD-569); a operação Musa pode pedir sem.
   */
  photoUrls: string[];
}

export type BucketRequestFormErrors = Partial<Record<
  'plannedDate' | 'locationType' | 'quantities' | 'contactName' | 'contactPhone' | 'observations' | 'photos',
  string
>> & {
  /** Um erro por retirada, na ordem dos campos; vazio onde o número está certo. */
  removalBucketNumbers?: Array<string | undefined>;
};

/** O que o contrato pede além do que vale para todo pedido. */
export interface BucketRequestRules {
  /** O contrato controla o número de cada caçamba: a retirada precisa dizer qual sai. */
  requiresNumber?: boolean;
  /**
   * O pedido leva ao menos uma foto: é a evidência que o transportador olha antes de aceitar
   * (EDD-569). Vale para o gerador; a operação Musa pede como no Hub, com foto opcional.
   */
  requiresPhoto?: boolean;
}

/** Limite do serviço para a nota de quem pediu, que reúne contato e observação. */
export const MAX_REQUESTER_NOTE = 1000;

/**
 * Teto do serviço por chamada (`BUCKET_BATCH_LIMIT` do musa-agreements): um envio vira no máximo
 * esse número de operações. Não é limite do contrato — só aparece para quem passa dele.
 */
export const MAX_BUCKETS_PER_REQUEST = 50;

/** Limite do serviço para o número da caçamba. */
export const MAX_BUCKET_NUMBER_LENGTH = 50;

export function initialBucketRequestForm(contact: { name?: string; phone?: string } = {}): BucketRequestFormState {
  return {
    placement: 0,
    removal: 0,
    removalBucketNumbers: [],
    locationType: null,
    plannedDate: '',
    period: null,
    contactName: contact.name ?? '',
    contactPhone: contact.phone ?? '',
    observations: '',
    photoUrls: [],
  };
}

const digits = (value: string) => value.replace(/\D/g, '');

const toBr = (day: string) => {
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
};

/**
 * Troca a quantidade de um campo. Na retirada, a lista de números acompanha: cresce com campos
 * vazios e, ao diminuir, perde os últimos.
 */
export function withBucketQuantity(
  form: BucketRequestFormState,
  field: BucketQuantityField,
  value: string | number,
): BucketRequestFormState {
  const count = toBucketCount(value);

  if (field === 'placement') return { ...form, placement: count };

  const numbers = form.removalBucketNumbers.slice(0, count);
  while (numbers.length < count) numbers.push('');

  return { ...form, removal: count, removalBucketNumbers: numbers };
}

/** Contato e observação viram a nota de quem pediu — é o que o transportador lê. */
export function buildRequesterNote(form: Pick<BucketRequestFormState, 'contactName' | 'contactPhone' | 'observations'>): string {
  const contact = `Acompanha: ${form.contactName.trim()} · ${form.contactPhone.trim()}`;
  const observations = form.observations.trim();
  return observations ? `${contact}\n${observations}` : contact;
}

/**
 * O que dá para conferir na tela. O serviço confere de novo (contrato, caçamba já agendada,
 * escopo) e é quem decide.
 */
export function validateBucketRequestForm(
  form: BucketRequestFormState,
  /**
   * `minDate`: primeiro dia que a antecedência da regra permite. `backdateFrom`: operação Musa —
   * aceita data passada a partir desse dia, sem antecedência.
   */
  limits: { today: string; minDate: string; maxDate?: string | null; backdateFrom?: string | null; minDateMessage?: string },
  rules: BucketRequestRules = {},
): BucketRequestFormErrors {
  const errors: BucketRequestFormErrors = {};
  const operations = countBucketOperations(form);

  if (!form.plannedDate) {
    errors.plannedDate = 'Escolha a data.';
  } else if (limits.backdateFrom) {
    if (form.plannedDate < limits.backdateFrom) {
      errors.plannedDate = `A data mais antiga aceita é ${toBr(limits.backdateFrom)}. Antes disso, a operação não poderia mais ser registrada.`;
    }
  } else if (form.plannedDate < limits.today) {
    errors.plannedDate = 'Não é possível pedir para data passada.';
  } else if (form.plannedDate < limits.minDate) {
    errors.plannedDate = limits.minDateMessage
      ?? `A antecedência mínima deste contrato permite pedidos a partir de ${toBr(limits.minDate)}.`;
  }

  if (!errors.plannedDate && limits.maxDate && form.plannedDate > limits.maxDate) {
    errors.plannedDate = `O contrato termina em ${toBr(limits.maxDate)}. Escolha uma data até esse dia.`;
  }

  if (form.placement > 0 && form.locationType == null) {
    errors.locationType = 'Informe onde a caçamba vai ficar.';
  }

  if (operations < 1) {
    errors.quantities = 'Informe quantas caçambas colocar ou retirar.';
  } else if (operations > MAX_BUCKETS_PER_REQUEST) {
    errors.quantities = `Peça no máximo ${MAX_BUCKETS_PER_REQUEST} operações por vez.`;
  }

  if (form.removal > 0) {
    const numberErrors = validateRemovalBucketNumbers(
      form.removalBucketNumbers.slice(0, form.removal),
      !!rules.requiresNumber,
    );
    if (numberErrors.some(Boolean)) errors.removalBucketNumbers = numberErrors;
  }

  if (form.contactName.trim().length < 4) errors.contactName = 'Informe o nome completo, com pelo menos 4 letras.';
  if (digits(form.contactPhone).length < 10) errors.contactPhone = 'Informe o telefone com DDD.';

  if (rules.requiresPhoto && form.photoUrls.length === 0) {
    errors.photos = 'Envie ao menos uma foto do local.';
  }

  if (buildRequesterNote(form).length > MAX_REQUESTER_NOTE) {
    errors.observations = `A observação passou do limite. Resuma em até ${MAX_REQUESTER_NOTE} caracteres, contando o contato.`;
  }

  return errors;
}

/**
 * O número informado fica dentro do limite do serviço e não se repete no pedido. Em branco só é
 * erro quando o contrato controla número; nos outros, o transportador informa no registro.
 */
function validateRemovalBucketNumbers(numbers: string[], required: boolean): Array<string | undefined> {
  const seen = new Set<string>();

  return numbers.map((raw) => {
    const number = raw.trim();
    if (!number) return required ? 'Informe o número da caçamba.' : undefined;
    if (number.length > MAX_BUCKET_NUMBER_LENGTH) return `Use até ${MAX_BUCKET_NUMBER_LENGTH} caracteres no número.`;
    if (seen.has(number)) return 'Número repetido: cada retirada é de uma caçamba diferente.';
    seen.add(number);
    return undefined;
  });
}

/** Uma operação do envio, com o que a tela precisa para dizer qual delas falhou. */
export interface BucketRequestItem {
  input: CreateBucketMovementInput;
  field: BucketQuantityField;
  /** "Retirada 2 de 3 (caçamba 4521)". */
  label: string;
}

/**
 * O pedido vira uma operação por caçamba: as retiradas e as colocações, todas com a mesma data e
 * turno. O serviço recebe tudo numa chamada só.
 *
 * A retirada leva o número quando ele foi preenchido — obrigatório no contrato que controla
 * número, opcional nos outros. Sem ele, quem diz qual caçamba saiu é o transportador, no registro.
 *
 * Uma data por pedido (MC-377): retirada para outro dia é outro pedido, nunca uma retirada
 * pendurada na colocação.
 *
 * As fotos são do pedido inteiro: vão em cada operação, porque cada uma vira uma movimentação.
 */
export function toBucketRequestItems(form: BucketRequestFormState): BucketRequestItem[] {
  const common = {
    plannedDate: form.plannedDate,
    period: form.period,
    requesterNote: buildRequesterNote(form),
  };

  const removals = Array.from({ length: form.removal }, (_, index): BucketRequestItem => {
    const bucketNumber = (form.removalBucketNumbers[index] ?? '').trim();
    return {
      input: {
        type: BucketMovementType.REMOVAL,
        ...common,
        ...(bucketNumber ? { bucketNumber } : {}),
        photoUrls: [...form.photoUrls],
      },
      field: 'removal',
      label: `Retirada ${index + 1} de ${form.removal}${bucketNumber ? ` (caçamba ${bucketNumber})` : ''}`,
    };
  });

  const placements = Array.from({ length: form.placement }, (_, index): BucketRequestItem => ({
    input: {
      type: BucketMovementType.PLACEMENT,
      ...common,
      locationType: form.locationType,
      photoUrls: [...form.photoUrls],
    },
    field: 'placement',
    label: `Colocação ${index + 1} de ${form.placement}`,
  }));

  return [...removals, ...placements];
}

export interface BucketRequestOutcome {
  succeeded: number;
  /** O que não entrou, com o motivo do serviço e um rótulo que a pessoa reconhece. */
  failures: Array<{ label: string; error: string }>;
  /** Avisos (limite, data) das operações que entraram — nunca bloqueiam. */
  warnings: BucketWarning[];
  /** O formulário só com o que falhou: reenviar não duplica o que já entrou. */
  remaining: BucketRequestFormState;
}

/**
 * Lê o resultado do lote. Cada item tem a sua transação no serviço, então parte pode entrar e
 * parte não: o que entrou já existe, e o formulário passa a pedir só o resto.
 */
export function readBucketBatchResult(
  form: BucketRequestFormState,
  items: BucketRequestItem[],
  result: BucketBatchResult,
): BucketRequestOutcome {
  const failures: BucketRequestOutcome['failures'] = [];
  const failed: Record<BucketQuantityField, number> = { removal: 0, placement: 0 };
  /** Os números das retiradas que não entraram, para o reenvio pedir as mesmas caçambas. */
  const failedRemovalNumbers: string[] = [];
  const warnings: BucketWarning[] = [];

  for (const item of result.results) {
    const sent = items[item.index];

    if (item.ok) {
      warnings.push(...((item as BucketMovementResult).warnings ?? []));
      continue;
    }

    const { error } = item as Extract<BucketBatchItem, { ok: false }>;

    if (sent) {
      failed[sent.field] += 1;
      if (sent.field === 'removal') failedRemovalNumbers.push(sent.input.bucketNumber ?? '');
    }
    failures.push({ label: sent?.label ?? `Operação ${item.index + 1} de ${items.length}`, error });
  }

  const remaining = {
    ...form,
    placement: failed.placement,
    removal: failed.removal,
    removalBucketNumbers: failedRemovalNumbers,
  };

  // avisos iguais (o mesmo limite estourado em cada colocação) aparecem uma vez
  const uniqueWarnings = [...new Map(warnings.map((w) => [w.message, w])).values()];

  return { succeeded: result.succeeded, failures, warnings: uniqueWarnings, remaining };
}